import "./CustomerAutocompleteV2.scss";

import { ListGroup, Spinner } from "react-bootstrap";
import { useEffect, useMemo, useRef, useState } from "react";

import CustomerAPI from "../../customer/CustomerAPI";
import HallAppToast from "../../../commons/HallAppToast";

const customerAPI = new CustomerAPI();

const isBlank = (v) => v == null || String(v).trim() === "";

function normalizeValueToSearch(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v?.name || v?.whatsapp || "";
}

function normalizeValueToObj(v) {
  if (!v) return { id: null, name: null, whatsapp: "" };
  if (typeof v === "string") return { id: null, name: null, whatsapp: v };
  return {
    id: v?.id ?? null,
    name: v?.name ?? null,
    whatsapp: v?.whatsapp ?? "",
    birthday: v?.birthday ?? null,
    lastVisit: v?.lastVisit ?? null,
  };
}

function CustomerAutocompleteV2({
  value,
  onChange,

  // ✅ opcional
  customerId = null,

  errors = {},
  name,
  touched = false,
  isRequired = false,

  label = "Cliente / WhatsApp",
  placeholder = "Cliente / WhatsApp",

  leftIcon,
  disabled = false,
  readOnly = false,
}) {
  const rootRef = useRef(null);

  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState(normalizeValueToSearch(value));
  const [filtered, setFiltered] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [showList, setShowList] = useState(false);

  // ✅ evita rodar prefill repetidamente pro mesmo id
  const lastPrefilledIdRef = useRef(null);

  const hasError = !!errors?.[name] && !!touched;

  // -------------------------
  // Load list
  // -------------------------
  useEffect(() => {
    getAllCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAllCustomers = async () => {
    try {
      setLoadingList(true);
      const response = await customerAPI.getCustomers();
      setCustomers(response.data || []);
    } catch (e) {
      HallAppToast.error("Erro ao carregar clientes");
    } finally {
      setLoadingList(false);
    }
  };

  // -------------------------
  // ✅ Prefill por customerId (SÓ DEPOIS DA LISTA CARREGAR)
  // -------------------------
  useEffect(() => {
    const run = async () => {
      if (!customerId) return;
      if (disabled || readOnly) return;

      // ✅ espera terminar a busca da lista
      if (loadingList) return;

      // ✅ ainda não carregou nada? (evita rodar cedo demais)
      if (!Array.isArray(customers)) return;

      // ✅ não repetir pro mesmo id
      if (String(lastPrefilledIdRef.current || "") === String(customerId)) return;

      const vObj = normalizeValueToObj(value);

      // se já está ok, só ajusta o texto e marca como preenchido
      const alreadyOk =
        String(vObj?.id || "") === String(customerId) &&
        !isBlank(vObj?.name) &&
        !isBlank(vObj?.whatsapp);

      if (alreadyOk) {
        setSearch(vObj.name || vObj.whatsapp || "");
        lastPrefilledIdRef.current = customerId;
        return;
      }

      // 1) tenta achar na lista primeiro
      const found = customers.find((c) => String(c?.id) === String(customerId));

      if (found) {
        const obj = {
          id: found.id,
          name: found.name ?? null,
          whatsapp: found.whatsapp ?? found.phone ?? "",
          birthday: found.birthday ?? null,
          lastVisit: found.lastVisit ?? null,
        };

        setSearch(obj?.name || obj?.whatsapp || "");

        const changed =
          String(vObj?.id || "") !== String(obj.id || "") ||
          String(vObj?.name || "") !== String(obj.name || "") ||
          String(vObj?.whatsapp || "") !== String(obj.whatsapp || "");

        if (changed) onChange?.(obj);

        lastPrefilledIdRef.current = customerId;
        return;
      }

      // 2) fallback: busca por id (caso lista não contenha)
      try {
        setLoadingPrefill(true);

        const resp = await customerAPI.findById(customerId);
        const c = resp?.data || resp;

        const obj = {
          id: c?.id ?? customerId,
          name: c?.name ?? null,
          whatsapp: c?.whatsapp ?? c?.phone ?? "",
          birthday: c?.birthday ?? null,
          lastVisit: c?.lastVisit ?? null,
        };

        setSearch(obj?.name || obj?.whatsapp || "");

        const changed =
          String(vObj?.id || "") !== String(obj.id || "") ||
          String(vObj?.name || "") !== String(obj.name || "") ||
          String(vObj?.whatsapp || "") !== String(obj.whatsapp || "");

        if (changed) onChange?.(obj);

        lastPrefilledIdRef.current = customerId;
      } catch (e) {
        HallAppToast.error("Não foi possível carregar o cliente pelo ID");
      } finally {
        setLoadingPrefill(false);
      }
    };

    run();
    // ✅ dependências importantes:
  }, [customerId, customers, loadingList, disabled, readOnly]); // (value/onChange ficam “capturados”, ok)

  // -------------------------
  // Sincroniza quando vier value de fora
  // -------------------------
  useEffect(() => {
    const next = normalizeValueToSearch(value);
    setSearch(next);

    if (isBlank(next)) {
      setFiltered([]);
      setShowList(false);
      return;
    }

    const results = customers.filter(
      (c) =>
        String(c.name || "").toLowerCase().includes(String(next).toLowerCase()) ||
        String(c.whatsapp || "").includes(String(next))
    );

    setFiltered(results);
  }, [value, customers]);

  // fecha dropdown ao clicar fora
  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setShowList(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const filterCustomers = (input) => {
    if (isBlank(input)) return [];
    const txt = String(input).toLowerCase();

    return customers.filter((c) => {
      const n = String(c.name || "").toLowerCase();
      const w = String(c.whatsapp || "");
      return n.includes(txt) || w.includes(String(input));
    });
  };

  function handleChange(e) {
    const input = e.target.value;
    setSearch(input);

    // digitando -> id null
    onChange?.({
      id: null,
      name: null,
      whatsapp: input,
    });

    const results = filterCustomers(input);
    setFiltered(results);
    setShowList(results.length > 0);
  }

  function handleSelect(cust) {
    setSearch(cust.name || cust.whatsapp || "");

    onChange?.({
      id: cust.id,
      name: cust.name,
      whatsapp: cust.whatsapp,
      birthday: cust.birthday ?? null,
      lastVisit: cust.lastVisit ?? null,
    });

    // ✅ se selecionou, já considera como “prefill” desse id
    lastPrefilledIdRef.current = cust?.id ?? null;

    setShowList(false);
  }

  const canOpen = useMemo(() => {
    if (disabled || readOnly) return false;
    return filtered.length > 0 && !isBlank(search);
  }, [filtered, search, disabled, readOnly]);

  const isLoadingAny = loadingList || loadingPrefill;

  return (
    <div
      ref={rootRef}
      className={`hallapp-autocompletev2 ${disabled ? "is-disabled" : ""} ${
        hasError ? "is-error" : ""
      }`}
    >
      <div className="hallapp-autocompletev2__label">
        <span>{label}</span>
        {isRequired && (
          <span className="hallapp-autocompletev2__required"> *(obrigatório)</span>
        )}
      </div>

      <div className={`hallapp-autocompletev2__wrap ${readOnly ? "is-readonly" : ""}`}>
        {leftIcon ? (
          <div className="hallapp-autocompletev2__iconLeft">{leftIcon}</div>
        ) : null}

        <input
          className="hallapp-autocompletev2__control"
          type="text"
          placeholder={placeholder}
          value={search}
          onChange={handleChange}
          onFocus={() => setShowList(canOpen)}
          disabled={disabled}
          readOnly={readOnly}
          autoComplete="off"
        />

        {isLoadingAny && (
          <div className="hallapp-autocompletev2__right">
            <Spinner size="sm" />
          </div>
        )}
      </div>

      <div className="hallapp-autocompletev2__meta">
        {hasError ? (
          <div className="hallapp-autocompletev2__error">{errors?.[name]}</div>
        ) : (
          <span />
        )}
      </div>

      {showList && (
        <div className="hallapp-autocompletev2__dropdown">
          <ListGroup className="hallapp-autocompletev2__list">
            {filtered.map((cust) => (
              <ListGroup.Item
                key={cust.id}
                action
                className="hallapp-autocompletev2__item"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(cust)}
              >
                <div className="hallapp-autocompletev2__itemTitle">{cust.name}</div>
                <div className="hallapp-autocompletev2__itemSub">{cust.whatsapp}</div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
  );
}

export default CustomerAutocompleteV2;